import { BaseResponse, MessageResponse, RequestConfig } from 'common/types/api'
import axios from 'core/api'

import { buildUrlWithParams } from 'utils/url'

import {
  GeneralType,
  Brand,
  Species,
  Breed,
  BreedBrand,
  BreedProduct,
  Markets,
  Market,
  MarketsVariables,
  BreedBrandParams,
  BreedProductParams,
  FeedPrograms,
  FeedProgramsVariables,
  FeedProgram,
  MasterData,
  DashboardMedicineRequest,
  DashboardMealRequest,
  DashboardFoodNoteRequest,
  DashboardShrimpHealthRequest,
  DashboardWaterTreatmentRequest,
  ThresholdResponse,
  DashboardDailyNoteRequest,
  DashboardWaterVibrioRequest,
  PondMediaRequest,
  DeletePondMediaRequest,
  UpdateTagPondMediaRequest,
  PartialDateList,
} from './types'

export const POND_REQUEST_CONFIG: RequestConfig = {
  getPonds: { method: 'GET', url: '/ponds' },
  createPond: { method: 'POST', url: '/ponds' },
  editPond: { method: 'PATCH', url: '/ponds' },
  removePond: { method: 'DELETE', url: '/ponds' },
  sortPonds: { method: 'PATCH', url: '/ponds/sort' },
  getFarmOverview: { method: 'GET', url: '/ponds/overview' },
}

export const CYCLE_REQUEST_CONFIG: RequestConfig = {
  getCycles: { method: 'GET', url: '/cycles/history' },
  getCycleById: { method: 'GET', url: '/cycles' },
  createCycle: { method: 'POST', url: '/cycles' },
  editCycle: { method: 'PATCH' },
  completeCycle: { method: 'PATCH', url: '/complete' },
  removeCycle: { method: 'DELETE' },
  getCycleSummary: { method: 'GET', url: '/summary' },
}

export const DASHBOARD_REQUEST_CONFIG: RequestConfig = {
  getDashboard: { method: 'GET', url: '/dashboard' },
  getPondDasboard: { method: 'GET', url: '/pond-dashboard' },
  getRevampPondDashboard: { method: 'GET', url: '/revamp-pond-dashboard' },
  createNewMedicineRow: { method: 'POST', url: '/revamp-pond-dashboard/round-number' },
  removeMedicineRow: { method: 'DELETE', url: '/revamp-pond-dashboard/round-number' },
}

export const EXPORT_REQUEST_CONFIG: RequestConfig = {
  exportSummary: { method: 'GET', url: '/summary-pdf', responseType: 'blob' },
  exportDaily: { method: 'GET', url: '/daily-pdf', responseType: 'blob' },
  exportFeatures: { method: 'GET', url: '/pdf', responseType: 'blob' },
}

export const DASHBOARD_REQUEST_CONFIG_LAB_TABLE: RequestConfig = {
  getLabTable: { method: 'GET', url: '/lab-table' },
  editTreatmentLabTable: { method: 'PATCH', url: '/lab-table/treatment' },
  editWaterVibrioLabTable: { method: 'PATCH', url: '/lab-table/water-vibrio' },
  editShrimpHealthLabTable: { method: 'PATCH', url: '/lab-table/shrimp-health' },
}

// NK-276: the all-ponds ไซส์กุ้ง matrix. Read-only — unlike the lab table there
// are no PATCH siblings, because every row is either recorded elsewhere (size,
// feed, vitamin) or derived (ADG, %แผล).
export const DASHBOARD_REQUEST_CONFIG_SHRIMP_SIZE_TABLE: RequestConfig = {
  getShrimpSizeTable: { method: 'GET', url: '/shrimp-size-table' },
}

export const MEDIA_ALBUM_REQUEST_CONFIG: RequestConfig = {
  getPondMedia: { method: 'GET', url: '/media' },
}

export const getPondTypes = () => axios.get<BaseResponse<GeneralType[]>>('/pond-types')

export const getShrimpBrands = () => axios.get<BaseResponse<Brand[]>>('/shrimp-brands')

export const getShrimpTypes = (brand: number) =>
  axios.get<BaseResponse<GeneralType[]>>(buildUrlWithParams('/shrimp-types', { brand }))

export const getSpecies = () => axios.get<BaseResponse<Species[]>>('/species')

export const getBreed = (speciesId: number | null) =>
  axios.get<BaseResponse<Breed[]>>(`/species/${speciesId}/breed`)

export const getBreedBrand = ({ speciesId, breedId }: BreedBrandParams) =>
  axios.get<BaseResponse<BreedBrand[]>>(`/species/${speciesId}/breed/${breedId}/breed-brand`)

export const getBreedProduct = ({ speciesId, breedId, breedBrandId }: BreedProductParams) =>
  axios.get<BaseResponse<BreedProduct[]>>(
    `/species/${speciesId}/breed/${breedId}/breed-brand/${breedBrandId}/breed-products`
  )

export const getFeedPrograms = () =>
  axios.get<BaseResponse<FeedPrograms[]>>(`/shrimp/feed-programs`)
export const getFeedProgramsById = (feedProgramId?: number) =>
  axios.get<BaseResponse<FeedProgram>>(`/shrimp/feed-programs/${feedProgramId}`)

export const editFeedPrograms = ({ feedProgramId, body }: FeedProgramsVariables) =>
  axios.patch<MessageResponse>(`/shrimp/feed-programs/${feedProgramId}`, {
    ...body,
  })
export const createFeedPrograms = ({ body }: FeedProgramsVariables) =>
  axios.post<MessageResponse>(`/shrimp/feed-programs`, { ...body })

export const getMarkets = (speciesId: number | null) =>
  axios.get<BaseResponse<Markets[]>>(buildUrlWithParams('/markets', { speciesId }))

export const getMarketsById = (marketId: number | null) =>
  axios.get<BaseResponse<Market>>(`/markets/${marketId}`)

export const editMarkets = ({ marketId, body }: MarketsVariables) =>
  axios.patch<MessageResponse>(`/markets/${marketId}`, { ...body })

export const createMarkets = ({ body }: MarketsVariables) =>
  axios.post<MessageResponse>(`/markets`, { ...body })

export const getPondAreaUnits = () => axios.get<BaseResponse<any[]>>(`/units/pond-area`)

export const getMedicine = (category: string) =>
  axios.get<BaseResponse<MasterData[]>>(`/farms/dashboard/medicine`, {
    params: { category },
  })

export const getFoodBrands = () =>
  axios.get<BaseResponse<MasterData[]>>(`/farms/dashboard/food-brand`)

export const getWaterColor = () =>
  axios.get<BaseResponse<MasterData[]>>(`/farms/dashboard/water-color`)

export const getWaterQualityThreshold = () =>
  axios.get<BaseResponse<ThresholdResponse>>(`/water-quality-threshold`)

export const createPondMedia = async ({
  farmId,
  pondId,
  cycleId,
  body,
  onUploadProgress: progressCallback,
}: PondMediaRequest & {
  onUploadProgress?: (event: ProgressEvent) => void
}) => {
  const formData = new FormData()
  formData.append('tag', body.tag)
  formData.append('media_date', body.mediaDate)

  body.files.forEach((file, index) => {
    formData.append(`medias[${index}].file`, file)
  })

  const config = {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: progressCallback,
  }

  return axios.post<MessageResponse>(
    `farms/${farmId}/ponds/${pondId}/cycles/${cycleId}/media/upload`,
    formData,
    config
  )
}

export const deletePondMedia = ({ farmId, pondId, body }: DeletePondMediaRequest) =>
  axios.delete<MessageResponse>(`/farms/${farmId}/ponds/${pondId}/media`, { data: body })

export const updateTagPondMedia = ({ farmId, pondId, body }: UpdateTagPondMediaRequest) =>
  axios.patch<MessageResponse>(`/farms/${farmId}/ponds/${pondId}/media`, body)

export const putDashboardMedicine = ({ body }: DashboardMedicineRequest) =>
  axios.put<MessageResponse>(`/farms/dashboard/medicine`, { ...body })

export const putDashboardMeal = ({ body }: DashboardMealRequest) =>
  axios.put<MessageResponse>(`/farms/dashboard/meal`, {
    ...body,
  })

export const putDashboardFoodNote = ({ body }: DashboardFoodNoteRequest) =>
  axios.put<MessageResponse>(`/farms/dashboard/food-note`, {
    ...body,
  })

export const putDashboardShrimpHealth = ({ body }: DashboardShrimpHealthRequest) =>
  axios.put<MessageResponse>(`/farms/dashboard/shrimp-health`, {
    ...body,
  })

export const putDashboardWaterTreatment = ({ body }: DashboardWaterTreatmentRequest) =>
  axios.put<MessageResponse>(`/farms/dashboard/water-treatment`, {
    ...body,
  })

export const putDashboardDailyNote = ({ body }: DashboardDailyNoteRequest) =>
  axios.put<MessageResponse>(`/farms/dashboard/daily-note`, {
    ...body,
  })

export const putDashboardWaterVibrio = ({ body }: DashboardWaterVibrioRequest) =>
  axios.put<MessageResponse>(`/farms/dashboard/water-vibrio`, {
    ...body,
  })

// NK-305: field-level update of one daily-table record — only the fields in the body
// change, `null` clears one. The PUTs above overwrite every field of the record.
export type DailyRecordKind =
  | 'meal'
  | 'shrimp-health'
  | 'water-treatment'
  | 'daily-note'
  | 'water-vibrio'

export interface DailyRecordPatchRequest {
  kind: DailyRecordKind
  body: { id: number } & Record<string, string | number | null>
}

export const patchDashboardRecord = ({ kind, body }: DailyRecordPatchRequest) =>
  axios.patch<MessageResponse>(`/farms/dashboard/${kind}`, body)

export const getPartialDateList = (cycleId: string) =>
  axios.get<BaseResponse<PartialDateList>>(`/partial-transfer/date-list/${cycleId}`)
